/* ==========================================================================
   Lalla — facets.js
   <facet-filters>: Shopify Storefront filtering & sorting without reloads.
   Each change re-renders the section through the Section Rendering API and
   replaces the [data-facets-part] regions; the URL stays shareable
   (history.pushState) and the back button works (popstate).
   ========================================================================== */

(() => {
  if (customElements.get('facet-filters')) return;
  const L = window.Lalla;

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();

      this.addEventListener('change', (event) => {
        const form = this.formFor(event.target);
        if (!form) return;
        clearTimeout(this.timer);
        // An invalid price range cancels any pending update (so the customer's
        // input and the error message are not wiped by a re-render).
        if (event.target.type === 'number' && !this.validatePrice(event.target)) return;
        this.timer = setTimeout(() => this.update(this.queryFrom(form)), 350);
      });

      this.addEventListener('submit', (event) => {
        const form = event.target.closest('form[data-facets-form]');
        if (!form) return;
        event.preventDefault();
        if (this.validatePrice(form.querySelector('input[type="number"]'))) this.update(this.queryFrom(form));
      });

      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-facet-link]');
        if (!link) return;
        event.preventDefault();
        this.update(new URL(link.href, window.location.origin).searchParams.toString());
      });

      this.onPopState = () => this.update(window.location.search.slice(1), { push: false });
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    formFor(element) {
      if (!element) return null;
      if (element.form && element.form.matches('[data-facets-form]')) return element.form;
      return element.closest('form[data-facets-form]');
    }

    queryFrom(form) {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        // Keep URLs clean: the default sort order is implied.
        if (value === '' || (key === 'sort_by' && value === this.dataset.defaultSort)) continue;
        params.append(key, value);
      }
      return params.toString();
    }

    validatePrice(input) {
      const wrapper = input && input.closest('[data-price-range]');
      if (!wrapper) return true;
      const [min, max] = Array.from(wrapper.querySelectorAll('input[type="number"]')).map((i) => parseFloat(i.value));
      const valid = Number.isNaN(min) || Number.isNaN(max) || min <= max;
      const error = wrapper.querySelector('[data-price-error]');
      if (error) error.hidden = valid;
      wrapper.querySelectorAll('input').forEach((i) => i.toggleAttribute('aria-invalid', !valid));
      if (!valid) L.announce(error ? error.textContent.trim() : '', { assertive: true });
      return valid;
    }

    async update(query, { push = true } = {}) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const grid = this.querySelector('[data-facets-part="grid"]');
      grid?.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');

      const pageUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
      const fetchUrl = `${window.location.pathname}?${query ? `${query}&` : ''}section_id=${encodeURIComponent(this.sectionId)}`;

      try {
        let html = this.cache.get(fetchUrl);
        if (!html) {
          const response = await fetch(fetchUrl, { signal: this.controller.signal });
          if (!response.ok) throw new Error(response.status);
          html = await response.text();
          this.cache.set(fetchUrl, html);
        }
        this.render(html);
        if (push) window.history.pushState({ facets: true }, '', pageUrl);
        this.announceCount();
        const top = this.querySelector('.collection__toolbar');
        if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ behavior: L.prefersReducedMotion() ? 'auto' : 'smooth' });
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Fall back to a normal page load: filtering must always work.
        window.location.assign(pageUrl);
      } finally {
        this.querySelector('[data-facets-part="grid"]')?.removeAttribute('aria-busy');
        this.classList.remove('is-loading');
      }
    }

    render(html) {
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('facet-filters');
      if (!fresh) throw new Error('facets: unexpected response');

      const active = document.activeElement && this.contains(document.activeElement) ? document.activeElement : null;
      const focusedId = active ? active.id : null;
      const typedValue = active && active.matches('input[type="number"], input[type="text"]') ? active.value : null;
      const openState = new Map(Array.from(this.querySelectorAll('details[id]')).map((d) => [d.id, d.open]));

      this.querySelectorAll('[data-facets-part]').forEach((part) => {
        const next = fresh.querySelector(`[data-facets-part="${part.dataset.facetsPart}"]`);
        if (next) part.innerHTML = next.innerHTML;
      });

      // Keep each filter group open/closed as the customer left it.
      this.querySelectorAll('details[id]').forEach((details) => {
        if (openState.has(details.id)) details.open = openState.get(details.id);
      });
      if (focusedId) {
        const next = document.getElementById(focusedId);
        if (next && typedValue !== null) next.value = typedValue;
        next?.focus({ preventScroll: true });
      }
      L.initReveal?.(this);
    }

    announceCount() {
      const count = Number(this.querySelector('[data-results-count]')?.dataset.resultsCount || 0);
      const template = count === 1 ? this.dataset.countOne : this.dataset.countOther;
      L.announce((template || '').replace('[count]', count));
    }
  }

  customElements.define('facet-filters', FacetFilters);
})();
